import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { ArrowLeft, ShieldCheck, CheckCircle2, AlertTriangle, XCircle, History, Calendar, Video, Phone, FileText, User, Download, Lock, Eye, EyeOff, Play, Image as ImageIcon, MessageSquare, Camera, FolderOpen, Inbox, Handshake, Megaphone, GaugeCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { clientApi } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// Phase 6 — client-side stage badge (read-only projection; no rule breakdown)
const BAND_TONE = {
  insufficient: 'bg-rose-50 text-rose-700 border-rose-200',
  needs_more: 'bg-amber-50 text-amber-700 border-amber-200',
  ready: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function StageBadge({ stage }) {
  const { t } = useTranslation();
  if (!stage) return null;
  const band = stage.band;
  const tone = BAND_TONE[band] || 'bg-slate-50 text-slate-600 border-slate-200';
  const bandLabel = band ? t(`talentFix.consent.badge.bands.${band}`, { defaultValue: band }) : '—';
  return (
    <div data-testid="tf6-client-stage-badge" className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${tone}`}>
      <GaugeCircle className="h-3.5 w-3.5" />
      <span className="font-medium">{t('talentFix.consent.badge.stage', { n: stage.current_stage })}</span>
      {stage.last_sufficiency_score !== null && stage.last_sufficiency_score !== undefined && (
        <>
          <span className="opacity-50 mx-1">·</span>
          <span>{stage.last_sufficiency_score}%</span>
          <span className="opacity-50 mx-1">·</span>
          <span>{bandLabel}</span>
        </>
      )}
    </div>
  );
}

function ClientStageHistoryAccordion({ items }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) {
    return <p className="text-xs italic text-slate-500" data-testid="tf6-client-stage-history-empty">{t('talentFix.consent.progression.empty')}</p>;
  }
  const fmt = (s) => (s ? String(s).slice(0, 16).replace('T', ' ') : '—');
  return (
    <div className="space-y-2" data-testid="tf6-client-stage-history">
      {items.map((row) => (
        <div key={row.id} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-800">
              {t('talentFix.consent.progression.stageRow', { from: row.from_stage ?? '—', to: row.to_stage })}
            </span>
            <Badge variant="outline" className="text-xs">
              {t(`talentFix.consent.progression.kinds.${row.approval_kind}`, { defaultValue: row.approval_kind })}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{fmt(row.approved_at)}</p>
        </div>
      ))}
    </div>
  );
}

const FORMAT_ICON = {
  video: Video,
  in_person: User,
  phone: Phone,
  async_written: FileText,
};

const DecisionBadge = ({ decision }) => {
  const { t } = useTranslation();
  if (!decision) return null;
  const tones = {
    approve: 'bg-emerald-100 text-emerald-700',
    request_changes: 'bg-amber-100 text-amber-700',
    reject: 'bg-rose-100 text-rose-700',
  };
  return (
    <Badge data-testid={`tf2-consent-decision-${decision}`} className={tones[decision] || 'bg-slate-100 text-slate-700'}>
      {t(`talentFix.consent.decisionBadge.${decision}`, { defaultValue: decision })}
    </Badge>
  );
};

const PlanSummary = ({ plan }) => {
  const { t } = useTranslation();
  if (!plan) return null;
  const kc = 'talentFix.consent';
  return (
    <Card data-testid="tf2-consent-plan-summary">
      <CardHeader>
        <CardTitle>{t(`${kc}.plan.title`)}</CardTitle>
        <CardDescription>{t(`${kc}.plan.desc`)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t(`${kc}.plan.hypothesis`)}</p>
          <p className="mt-1 whitespace-pre-wrap text-slate-800" data-testid="tf2-consent-hypothesis">
            {plan.hypothesis || t(`${kc}.plan.noHypothesis`)}
          </p>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t(`${kc}.plan.interviewees`, { n: (plan.interview_roster || []).length })}
          </p>
          <ul className="mt-2 space-y-2" data-testid="tf2-consent-roster">
            {(plan.interview_roster || []).map((r, i) => {
              const Icon = FORMAT_ICON[r.format] || User;
              return (
                <li key={i} className="rounded border border-slate-200 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-800">{r.target_label}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      <Icon className="h-3.5 w-3.5" />
                      {t(`${kc}.formats.${r.format}`, { defaultValue: (r.format || '').replace(/_/g, ' ') })}
                    </span>
                  </div>
                  {r.target_role && <p className="mt-0.5 text-xs text-slate-500">{t(`${kc}.plan.role`)}: {r.target_role}</p>}
                  {r.purpose && <p className="mt-1 text-xs text-slate-600">{t(`${kc}.plan.purpose`)}: {r.purpose}</p>}
                  {r.proposed_date && (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />{r.proposed_date}
                    </p>
                  )}
                </li>
              );
            })}
            {(plan.interview_roster || []).length === 0 && (
              <li className="text-xs text-slate-500">{t(`${kc}.plan.noInterviews`)}</li>
            )}
          </ul>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t(`${kc}.plan.observations`, { n: (plan.observation_plan || []).length })}
          </p>
          <ul className="mt-2 space-y-1.5" data-testid="tf2-consent-observations">
            {(plan.observation_plan || []).map((o, i) => (
              <li key={i} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                <span className="text-slate-800">{o.target_label}</span>
                <span className="text-xs text-slate-500">
                  {t(`${kc}.obsKinds.${o.kind}`, { defaultValue: (o.kind || '').replace(/_/g, ' ') })}
                </span>
              </li>
            ))}
            {(plan.observation_plan || []).length === 0 && (
              <li className="text-xs text-slate-500">{t(`${kc}.plan.noObservations`)}</li>
            )}
          </ul>
        </div>

        {plan.confidentiality_plan_note && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t(`${kc}.plan.confidentiality`)}</p>
            <p className="mt-1 whitespace-pre-wrap text-slate-700">{plan.confidentiality_plan_note}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-xs text-slate-600">
          <div>
            <p className="text-slate-400">{t(`${kc}.plan.leadAdvisor`)}</p>
            <p>{plan.lead_advisor_name || '—'}</p>
          </div>
          <div>
            <p className="text-slate-400">{t(`${kc}.plan.duration`)}</p>
            <p>{plan.estimated_duration_weeks ? t(`${kc}.plan.weeks`, { count: plan.estimated_duration_weeks }) : '—'}</p>
          </div>
          {plan.cosigned_at && (
            <div className="col-span-2 inline-flex items-center gap-1.5 rounded bg-blue-50 px-3 py-2 text-blue-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              {plan.cosigned_by_name
                ? t(`${kc}.plan.cosignedBy`, { name: plan.cosigned_by_name, date: String(plan.cosigned_at).slice(0, 10) })
                : t(`${kc}.plan.cosigned`, { date: String(plan.cosigned_at).slice(0, 10) })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ===== Shared evidence section (Phase 4a — Adj-4 / client-facing) =====

const CONF_TONE = {
  client_visible: 'bg-emerald-100 text-emerald-700',
  shared_with_client: 'bg-teal-100 text-teal-700',
  restricted: 'bg-slate-100 text-slate-600',
};

const FAMILY_ICON = {
  document: FileText,
  media: ImageIcon,
  communication: MessageSquare,
  testimony: User,
  snapshot: Camera,
  investigation_artefact: FolderOpen,
  external: FileText,
};

function formatSharedAt(value, lang) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 16).replace('T', ' ');
    return d.toLocaleString(lang?.startsWith('es') ? 'es' : 'en', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(value).slice(0, 16).replace('T', ' ');
  }
}

function SharedEvidenceRow({ ev, unseenInfo, onMarkSeen, onAfterSeen }) {
  const { t, i18n } = useTranslation();
  const kc = 'talentFix.consent.evidence';
  const isRedacted = !!ev.title_redacted;
  const level = ev.confidentiality_treatment || (isRedacted ? 'restricted' : 'shared_with_client');
  const confTone = CONF_TONE[level] || CONF_TONE.restricted;
  const confLabel = t(`${kc}.conf.${level}`, { defaultValue: t(`${kc}.conf.restricted`) });
  const Icon = isRedacted ? Lock : (FAMILY_ICON[ev.family] || FileText);
  const isDownloadable = !isRedacted && (level === 'client_visible' || level === 'shared_with_client') && !!ev.attachment_uri;
  const downloadUrl = `${api.defaults.baseURL.replace(/\/$/, '')}/client/talentfix/evidence/${ev.id}/download`;
  const previewBase = `${api.defaults.baseURL.replace(/\/$/, '')}/client/talentfix/evidence/${ev.id}`;
  const isMedia = !isRedacted && ev.family === 'media' && ['video', 'audio', 'image'].includes(ev.kind);
  const unseen = !!unseenInfo;
  const [previewOpen, setPreviewOpen] = useState(false);
  // Prefer the fresh share-event metadata for unseen rows, fall back to the evidence document's
  // first_shared_* fields so "Shared by / Shared at" remain visible even after Mark-as-seen.
  const sharedAt = unseenInfo?.shared_at || ev.first_shared_at;
  const sharedBy = unseenInfo?.shared_by_name || ev.first_shared_by_name;
  const thumbUrl = ev.thumbnail_uri && isMedia ? `${previewBase}/thumbnail` : null;

  const handleMarkSeen = async () => {
    try {
      await onMarkSeen(ev.id);
      toast.success(t(`${kc}.markedSeen`));
      onAfterSeen?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(`${kc}.markSeenError`));
    }
  };

  return (
    <div
      data-testid={`tf4-client-evidence-row-${ev.id}`}
      className={`rounded-lg border p-3 transition ${unseen ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white'}`}
    >
      <div className="flex items-start justify-between gap-3">
        {thumbUrl && (
          <img
            data-testid={`tf4-client-evidence-thumb-${ev.id}`}
            src={thumbUrl}
            alt=""
            className="h-14 w-20 shrink-0 rounded border border-slate-200 object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Icon className={`h-4 w-4 ${isRedacted ? 'text-slate-400' : 'text-slate-600'}`} />
            <span
              className={`truncate font-medium ${isRedacted ? 'italic text-slate-500' : 'text-slate-900'}`}
              data-testid={`tf4-client-evidence-title-${ev.id}`}
            >
              {isRedacted ? t(`${kc}.restrictedTitle`) : (ev.title || t(`${kc}.untitled`))}
            </span>
            <Badge className={confTone} data-testid={`tf4-client-evidence-conf-${ev.id}`}>
              {confLabel}
            </Badge>
            {unseen ? (
              <Badge className="bg-emerald-600 text-white" data-testid={`tf4-client-evidence-new-${ev.id}`}>{t(`${kc}.new`)}</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500" data-testid={`tf4-client-evidence-seen-${ev.id}`}>
                <Eye className="mr-1 h-3 w-3" />{t(`${kc}.seen`)}
              </Badge>
            )}
            {ev.version_number > 1 && (
              <Badge variant="outline">{t(`${kc}.version`, { n: ev.version_number })}</Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
            <span>{t(`${kc}.kinds.${ev.kind}`, { defaultValue: (ev.kind || 'evidence').replace(/_/g, ' ') })}</span>
            {sharedAt && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />{t(`${kc}.sharedAt`, { date: formatSharedAt(sharedAt, i18n.language) })}
              </span>
            )}
            {sharedBy && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" />{t(`${kc}.byName`, { name: sharedBy })}
              </span>
            )}
            {isRedacted && (
              <span className="text-slate-400">{t(`${kc}.restrictedWhy`)}</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          {isMedia && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPreviewOpen((p) => !p)}
              data-testid={`tf4-client-evidence-preview-${ev.id}`}
            >
              {previewOpen
                ? <><EyeOff className="mr-1 h-3.5 w-3.5" />{t(`${kc}.hide`)}</>
                : <><Play className="mr-1 h-3.5 w-3.5" />{t(`${kc}.preview`)}</>}
            </Button>
          )}
          {isDownloadable && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`tf4-client-evidence-download-${ev.id}`}
            >
              <Button size="sm" variant="outline" className="w-full">
                <Download className="mr-1 h-3.5 w-3.5" />{t(`${kc}.open`)}
              </Button>
            </a>
          )}
          {unseen && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleMarkSeen}
              data-testid={`tf4-client-evidence-mark-seen-${ev.id}`}
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />{t(`${kc}.markSeen`)}
            </Button>
          )}
        </div>
      </div>
      {previewOpen && isMedia && (
        <div className="mt-3" data-testid={`tf4-client-evidence-preview-block-${ev.id}`}>
          {ev.kind === 'video' && (
            <video
              data-testid={`tf4-client-video-${ev.id}`}
              controls
              preload="metadata"
              poster={thumbUrl || undefined}
              className="w-full max-h-96 rounded border border-slate-200 bg-black"
              src={`${previewBase}/download.v2`}
            />
          )}
          {ev.kind === 'audio' && (
            <audio
              data-testid={`tf4-client-audio-${ev.id}`}
              controls
              preload="metadata"
              className="w-full"
              src={`${previewBase}/download.v2`}
            />
          )}
          {ev.kind === 'image' && (
            <img
              data-testid={`tf4-client-image-${ev.id}`}
              src={`${previewBase}/download.v2`}
              alt={ev.title}
              className="max-h-96 cursor-zoom-in rounded border border-slate-200 object-contain"
              onClick={() => window.open(`${previewBase}/download.v2`, '_blank')}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SharedEvidenceSection({ caseId }) {
  const { t } = useTranslation();
  const kc = 'talentFix.consent.evidence';
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [unseenById, setUnseenById] = useState({}); // {evidence_id: {shared_at, shared_by_name}}

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, recentRes] = await Promise.all([
        clientApi.tfListEvidence(caseId),
        clientApi.tfRecentlySharedEvidence(caseId),
      ]);
      const unseenMap = {};
      (recentRes?.events || []).forEach((e) => {
        if (!unseenMap[e.evidence_id]) {
          unseenMap[e.evidence_id] = {
            shared_at: e.shared_at,
            shared_by_name: e.shared_by_name,
          };
        }
      });
      setItems(listRes?.items || []);
      setUnseenById(unseenMap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(`${kc}.loadError`));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [caseId]);

  const markSeen = async (eid) => {
    await clientApi.tfMarkEvidenceSeen(eid);
    setUnseenById((prev) => {
      const next = { ...prev };
      delete next[eid];
      return next;
    });
  };

  const unseenCount = Object.keys(unseenById).length;
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const aNew = unseenById[a.id] ? 0 : 1;
      const bNew = unseenById[b.id] ? 0 : 1;
      if (aNew !== bNew) return aNew - bNew;
      return 0;
    });
  }, [items, unseenById]);

  return (
    <Card data-testid="tf4-client-shared-evidence-section">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="inline-flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            {t(`${kc}.title`)}
            {unseenCount > 0 && (
              <Badge className="bg-emerald-600 text-white" data-testid="tf4-client-shared-unseen-count">
                {t(`${kc}.newCount`, { count: unseenCount })}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>{t(`${kc}.desc`)}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="py-4 text-sm text-slate-500" data-testid="tf4-client-shared-loading">{t(`${kc}.loading`)}</p>
        ) : sorted.length === 0 ? (
          <div
            data-testid="tf4-client-shared-empty"
            className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center"
          >
            <Inbox className="mb-2 h-8 w-8 text-slate-400" />
            <p className="text-sm font-medium text-slate-700">{t(`${kc}.emptyTitle`)}</p>
            <p className="mt-1 max-w-md text-xs text-slate-500">
              <Trans i18nKey={`${kc}.emptyDesc`} components={{ 1: <em />, 3: <em /> }} />
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="tf4-client-shared-list">
            {sorted.map((ev) => (
              <SharedEvidenceRow
                key={ev.id}
                ev={ev}
                unseenInfo={unseenById[ev.id]}
                onMarkSeen={markSeen}
                onAfterSeen={load}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ===== Mediation outcomes section (Phase 5 — client read-only) =====

const M_OUTCOME_META = {
  resolved: { tone: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  partially_resolved: { tone: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
  not_resolved: { tone: 'bg-rose-100 text-rose-700', Icon: XCircle },
  escalated: { tone: 'bg-purple-100 text-purple-700', Icon: Megaphone },
};
const M_COMMITMENT_TONE = {
  open: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
  missed: 'bg-rose-100 text-rose-700',
};

function MediationOutcomesSection({ caseId }) {
  const { t, i18n } = useTranslation();
  const kc = 'talentFix.consent.mediation';
  const [loading, setLoading] = useState(true);
  const [mediations, setMediations] = useState([]);
  const [commitments, setCommitments] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        clientApi.tfListMediations(caseId),
        clientApi.tfListMediationCommitments(caseId),
      ]);
      setMediations(m?.items || []);
      setCommitments(c?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(`${kc}.loadError`));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [caseId]);

  const aggCompliance = useMemo(() => {
    let tt = 0, d = 0;
    mediations.forEach((m) => {
      const cc = m.commitment_compliance || {};
      tt += cc.total || 0; d += cc.completed || 0;
    });
    return { total: tt, completed: d, percentage: tt ? Math.round((d / tt) * 1000) / 10 : 0.0 };
  }, [mediations]);

  return (
    <Card data-testid="tf5-client-mediation-section">
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Handshake className="h-5 w-5 text-teal-600" />
          {t(`${kc}.title`)}
          {aggCompliance.total > 0 && (
            <Badge className="bg-teal-100 text-teal-700" data-testid="tf5-client-aggregate-compliance">
              {t(`${kc}.commitmentsBadge`, { done: aggCompliance.completed, total: aggCompliance.total, pct: aggCompliance.percentage })}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>{t(`${kc}.desc`)}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="py-4 text-sm text-slate-500" data-testid="tf5-client-mediation-loading">{t(`${kc}.loading`)}</p>
        ) : mediations.length === 0 ? (
          <div
            data-testid="tf5-client-mediation-empty"
            className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center"
          >
            <Inbox className="mb-2 h-8 w-8 text-slate-400" />
            <p className="text-sm font-medium text-slate-700">{t(`${kc}.emptyTitle`)}</p>
            <p className="mt-1 max-w-md text-xs text-slate-500">{t(`${kc}.emptyDesc`)}</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="tf5-client-mediation-list">
            {mediations.map((m) => {
              const meta = m.outcome ? M_OUTCOME_META[m.outcome] : null;
              const cc = m.commitment_compliance || { total: 0, completed: 0, percentage: 0 };
              const rows = commitments.filter((c) => c.mediation_id === m.id);
              return (
                <div
                  key={m.id}
                  data-testid={`tf5-client-mediation-row-${m.id}`}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-slate-800">
                          {t(`${kc}.kinds.${m.kind}`, { defaultValue: (m.kind || '').replace(/_/g, ' ') })}
                        </span>
                        {meta && (
                          <Badge className={meta.tone} data-testid={`tf5-client-mediation-outcome-${m.id}`}>
                            <meta.Icon className="mr-1 h-3 w-3" />
                            {t(`${kc}.outcomes.${m.outcome}`, { defaultValue: m.outcome })}
                          </Badge>
                        )}
                      </div>
                      {m.date_time && (
                        <p className="text-xs text-slate-500 mt-0.5">{formatSharedAt(m.date_time, i18n.language)}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">{t(`${kc}.commitments`)}</div>
                      <div className="text-sm font-medium">
                        {cc.completed}/{cc.total} · {cc.percentage}%
                      </div>
                    </div>
                  </div>
                  {m.client_visible_outcome_summary && (
                    <p
                      className="mt-2 text-sm text-slate-700 whitespace-pre-line"
                      data-testid={`tf5-client-mediation-summary-${m.id}`}
                    >
                      {m.client_visible_outcome_summary}
                    </p>
                  )}
                  {rows.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {rows.map((c) => (
                        <li
                          key={c.commitment_id}
                          data-testid={`tf5-client-commitment-row-${c.commitment_id}`}
                          className="flex items-start gap-2 text-xs"
                        >
                          <Badge className={M_COMMITMENT_TONE[c.status] || 'bg-slate-100 text-slate-500'}>
                            {t(`${kc}.cstatus.${c.status}`, { defaultValue: c.status })}
                          </Badge>
                          <div className="flex-1">
                            <div className="text-slate-700">{c.description}</div>
                            <div className="text-slate-400">
                              {c.owner_label && <>{t(`${kc}.owner`, { name: c.owner_label })} · </>}
                              {c.due_date && <>{t(`${kc}.due`, { date: c.due_date.slice(0, 10) })} · </>}
                              {(c.department_attribution || []).join(', ')}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ClientTalentFixConsent() {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const kc = 'talentFix.consent';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(null); // 'approve' | 'request_changes' | 'reject'
  const [stage, setStage] = useState(null);
  const [stageHistory, setStageHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/client/talentfix/cases/${caseId}/consent`);
      setData(r.data);
      // Phase 6 — pull stage badge + history (non-blocking)
      try {
        const [s, h] = await Promise.all([
          clientApi.tf6ClientStage(caseId),
          clientApi.tf6ClientStageHistory(caseId),
        ]);
        setStage(s);
        setStageHistory(h.items || []);
      } catch (_) { /* phase 6 surfaces optional */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(`${kc}.loadError`));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [caseId]);

  const submitDecision = async (decision) => {
    if (decision !== 'approve' && (!note.trim() || note.trim().length < 5)) {
      toast.error(t(`${kc}.decision.noteRequired`));
      return;
    }
    setSubmitting(decision);
    try {
      await api.post(`/client/talentfix/cases/${caseId}/consent`, { decision, note: note.trim() || null });
      const messages = {
        approve: t(`${kc}.decision.successApprove`),
        request_changes: t(`${kc}.decision.successRequestChanges`),
        reject: t(`${kc}.decision.successReject`),
      };
      toast.success(messages[decision]);
      setNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(`${kc}.decision.failed`));
    } finally { setSubmitting(null); }
  };

  const canDecide = data?.can_decide;
  const latest = data?.latest_decision;

  const headline = useMemo(() => {
    if (!data) return '';
    if (data.case_stage === 'stage2_assessment' && !data.plan_ready) {
      return t(`${kc}.headlineDrafting`);
    }
    if (canDecide) return t(`${kc}.headlineDecide`);
    if (data?.plan?.status === 'consent_granted') return t(`${kc}.headlineGranted`);
    if (data?.plan?.status === 'needs_revision') return t(`${kc}.headlineRevising`);
    return '';
  }, [data, canDecide, t]);

  // Human-readable label for the internal workflow-stage code (stored value untouched)
  const stageLabel = data?.case_stage
    ? t(`talentFix.stage.${data.case_stage}`, { defaultValue: data.case_stage })
    : '—';

  if (loading) return <div className="p-6 text-sm text-slate-500">{t(`${kc}.loading`)}</div>;
  if (!data) return <div className="p-6 text-sm text-rose-600">{t(`${kc}.caseNotFound`)}</div>;

  return (
    <div className="space-y-6 p-6" data-testid="tf2-client-consent-page">
      <div>
        <Link to={`/client/talentfix`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" />{t(`${kc}.backToCases`)}
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">{t(`${kc}.title`)}</h1>
        <p className="text-sm text-slate-500" data-testid="tf2-consent-case-subtitle">
          {data.case_title || t(`${kc}.caseFallback`)} · {stageLabel}
        </p>
        {stage && (
          <div className="mt-3">
            <StageBadge stage={stage} />
          </div>
        )}
        {headline && (
          <p data-testid="tf2-consent-headline" className="mt-3 rounded border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {headline}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[2fr_1fr]">
        {/* Centre — plan summary */}
        <div>
          {data.plan_ready
            ? <PlanSummary plan={data.plan} />
            : (
              <Card>
                <CardHeader>
                  <CardTitle>{t(`${kc}.noPlan.title`)}</CardTitle>
                  <CardDescription>{t(`${kc}.noPlan.desc`)}</CardDescription>
                </CardHeader>
              </Card>
            )
          }
        </div>

        {/* Right rail — decision actions + history */}
        <div className="space-y-4">
          <Card data-testid="tf2-consent-actions">
            <CardHeader>
              <CardTitle>{t(`${kc}.decision.title`)}</CardTitle>
              <CardDescription>
                {canDecide
                  ? t(`${kc}.decision.descDecide`)
                  : (latest
                      ? t(`${kc}.decision.descResponded`)
                      : (data?.plan?.status === 'consent_granted'
                          ? t(`${kc}.decision.descGranted`)
                          : (data?.plan?.status === 'needs_revision'
                              ? t(`${kc}.decision.descRevising`)
                              : t(`${kc}.decision.descWaiting`))))}
              </CardDescription>
            </CardHeader>
            {canDecide ? (
              <CardContent className="space-y-3">
                <Textarea
                  data-testid="tf2-consent-note"
                  rows={4}
                  placeholder={t(`${kc}.decision.notePlaceholder`)}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={!!submitting}
                />
                <div className="flex flex-col gap-2">
                  <Button
                    data-testid="tf2-consent-approve-btn"
                    disabled={!!submitting}
                    onClick={() => submitDecision('approve')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    {submitting === 'approve' ? t(`${kc}.decision.approving`) : t(`${kc}.decision.approve`)}
                  </Button>
                  <Button
                    data-testid="tf2-consent-request-changes-btn"
                    variant="outline"
                    disabled={!!submitting}
                    onClick={() => submitDecision('request_changes')}
                  >
                    <AlertTriangle className="mr-1.5 h-4 w-4" />
                    {submitting === 'request_changes' ? t(`${kc}.decision.sending`) : t(`${kc}.decision.requestChanges`)}
                  </Button>
                  <Button
                    data-testid="tf2-consent-reject-btn"
                    variant="outline"
                    disabled={!!submitting}
                    onClick={() => submitDecision('reject')}
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <XCircle className="mr-1.5 h-4 w-4" />
                    {submitting === 'reject' ? t(`${kc}.decision.rejecting`) : t(`${kc}.decision.reject`)}
                  </Button>
                </div>
              </CardContent>
            ) : (
              <CardContent>
                <div
                  data-testid="tf2-consent-locked"
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600"
                >
                  {data?.plan?.status === 'consent_granted'
                    ? t(`${kc}.decision.lockedGranted`)
                    : (data?.plan?.status === 'needs_revision'
                        ? t(`${kc}.decision.lockedRevising`)
                        : t(`${kc}.decision.lockedNoPlan`))}
                </div>
              </CardContent>
            )}
          </Card>

          {latest && (
            <Card data-testid="tf2-consent-latest">
              <CardHeader>
                <CardTitle className="text-base">{t(`${kc}.latest.title`)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <DecisionBadge decision={latest.decision} />
                  <span className="text-xs text-slate-500">{String(latest.granted_at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
                <p className="text-xs text-slate-500">{t(`${kc}.latest.by`, { name: latest.granted_by_name || '—' })}</p>
                {latest.note && <p className="rounded bg-slate-50 px-3 py-2 text-slate-700">{latest.note}</p>}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="inline-flex items-center gap-1.5 text-base">
                <History className="h-4 w-4" /> {t(`${kc}.next.title`)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs text-slate-600">
              <p><span className="font-semibold text-emerald-700">{t(`${kc}.next.approveLabel`)}</span> {t(`${kc}.next.approveText`)}</p>
              <p><span className="font-semibold text-amber-700">{t(`${kc}.next.changesLabel`)}</span> {t(`${kc}.next.changesText`)}</p>
              <p><span className="font-semibold text-rose-700">{t(`${kc}.next.rejectLabel`)}</span> {t(`${kc}.next.rejectText`)}</p>
            </CardContent>
          </Card>

          {stage && (
            <Card data-testid="tf6-client-stage-card">
              <CardHeader>
                <CardTitle className="inline-flex items-center gap-1.5 text-base">
                  <GaugeCircle className="h-4 w-4" /> {t(`${kc}.progression.title`)}
                </CardTitle>
                <CardDescription>{t(`${kc}.progression.desc`)}</CardDescription>
              </CardHeader>
              <CardContent>
                <ClientStageHistoryAccordion items={stageHistory} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <SharedEvidenceSection caseId={caseId} />
      <MediationOutcomesSection caseId={caseId} />
    </div>
  );
}
